import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil'
import Fuse from 'fuse.js'
import { Trash } from '@emotion-icons/fa-solid/Trash'
import styled from '@emotion/styled'
import { UserList, UserListItem as TUserListItem } from 'src/common-services'
import { getUserListItemSubtitle, getUserListItemTitle } from 'src/utils/user-lists'
import { USER_LIST_ITEM_ICONS } from 'src/components/user-lists-modal/constants'
import {
  ListItemWrapper,
  ListItemIconWrapper,
  ListItemName,
  ListItemDescription,
  DeleteActionWrapper,
  UserListsWrapper,
} from 'src/components/user-lists-modal/components'
import { Colors } from 'src/components/theme'
import { useRemoveUserListItem } from 'src/components/user-lists-popover/hooks'
import { Button as UnstyledButton } from 'src/components/button'
import { googleSearchString } from 'src/atoms/search'
import { listFilter } from 'src/atoms/user-lists/user-lists-modal'
import { ListEmptyState } from 'src/components/user-lists-modal/list-empty-state'
import { useRouter } from 'next/router'
import { Snackbar, Typography } from '@mui/material'
import { CheckCircle } from '@emotion-icons/material/CheckCircle'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { useMapExplorerChoice } from 'src/views/map-explorer/sidebar/search/use-choice'
import { useAtomValue } from 'jotai'

import * as userListsModalAtoms from 'src/atoms/user-lists/user-lists-modal'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'
import { SelectedEntity } from 'src/v2/types/core'
import { browserLogger } from 'src/utils/browser-logger'

const Button = styled(UnstyledButton)`
  box-shadow: none;

  &:hover,
  &:active {
    box-shadow: none;
  }
`

const SnackContent = styled.div`
  background-color: ${Colors.primaryGreen100};
  color: ${Colors.neutral500};
  border: 1px solid ${Colors.primaryGreen400};
  border-radius: 6px;
  padding: 16px 16px;
  display: flex;
  gap: 8px;
`

type UserListItemProps = {
  onClickHandler: (item: TUserListItem) => void
  onClickDelete: (item: TUserListItem) => (evt: React.SyntheticEvent<HTMLDivElement>) => void
  item: TUserListItem
  disabled?: boolean
  disabledTooltip?: string
}

const UserListItem: FC<UserListItemProps> = ({ item, onClickDelete, onClickHandler, disabled, disabledTooltip }) => {
  const Icon = USER_LIST_ITEM_ICONS[item.type]
  const title = getUserListItemTitle(item)
  const subtitle = getUserListItemSubtitle(item)

  const onClick = disabled ? undefined : () => onClickHandler(item)
  return (
    <DarkTooltip title={disabledTooltip} arrow={false} placement="top">
      <ListItemWrapper key={item.id} onClick={onClick} disabled={disabled ? 1 : 0}>
        <ListItemIconWrapper>
          <Icon size={12} />
        </ListItemIconWrapper>
        <ListItemName>{title}</ListItemName>
        <ListItemDescription>{subtitle}</ListItemDescription>
        <DeleteActionWrapper onClick={onClickDelete(item)}>
          <Trash size={12} color={Colors.redDark} />
        </DeleteActionWrapper>
      </ListItemWrapper>
    </DarkTooltip>
  )
}

const getListItemTypeFromSupportedSelections = (
  supportedSelections: SelectedEntity['type'][]
): TUserListItem['type'][] => {
  const listItemTypes: TUserListItem['type'][] = []
  if (supportedSelections.includes('SELECTED_LOCATION') || supportedSelections.includes('SELECTED_SHOPPING_CENTER')) {
    listItemTypes.push('location')
  }
  if (supportedSelections.includes('SELECTED_ADDRESS')) {
    listItemTypes.push('place')
  }
  if (supportedSelections.includes('SELECTED_BRAND')) {
    // Currently not supported
    // listItemTypes.push('brand')
  }
  if (supportedSelections.includes('SELECTED_AREA')) {
    listItemTypes.push('custom-area')
  }
  return listItemTypes
}

export const UserListItems: FC = () => {
  const [filter, setFilter] = useRecoilState(listFilter)
  const setGoogleSearchString = useSetRecoilState(googleSearchString)
  const setUserListsModalOpen = useSetRecoilState(userListsModalAtoms.open)
  const [saveItemSuccessMessage, setSaveItemSuccessMessage] = useRecoilState(userListsModalAtoms.saveItemSuccessMessage)
  const selectedList = useRecoilValue(userListsModalAtoms.selectedList) as UserList
  const router = useRouter()
  const isMapBuilderPage = router.pathname.includes('map-builder')
  const { mutate: removeUserListItem } = useRemoveUserListItem()
  const { selectAddress, selectLocation, selectArea } = useMapExplorerNavigation()
  const { chooseLocation, chooseAddress } = useMapExplorerChoice()
  const supportedSelections = useAtomValue(selectorAtoms.supportedSelections)
  const inEntitySelector = Boolean(supportedSelections)

  const listItemTypes = useMemo(
    () => (supportedSelections ? getListItemTypeFromSupportedSelections(supportedSelections) : []),
    [supportedSelections]
  )

  const fuse = useMemo(
    () =>
      new Fuse(selectedList.items, {
        includeScore: true,
        keys: ['name'],
      }),
    [selectedList.items]
  )

  useEffect(() => {
    return () => setSaveItemSuccessMessage(undefined)
  }, [])

  const filteredList = filter !== '' ? fuse.search(filter).map((result) => result.item) : selectedList.items

  const handleClickDeleteItem = (item: TUserListItem) => (evt: React.SyntheticEvent<HTMLDivElement>) => {
    evt.stopPropagation()
    removeUserListItem(selectedList, item)
  }

  const handleClickItem = useCallback(
    async (item: TUserListItem) => {
      if (isMapBuilderPage && item.type !== 'place') {
        return
      }

      setUserListsModalOpen(false)
      switch (item.type) {
        case 'place':
          inEntitySelector ? chooseAddress(item.data.googlePlaceId) : await selectAddress(item.data.googlePlaceId)
          break
        case 'location':
          inEntitySelector ? chooseLocation(item.data.locationId) : await selectLocation(item.data.locationId)
          break
        case 'custom-area':
          // For now, we don't support choosing custom areas in the selector
          if (inEntitySelector) {
            break
          }
          browserLogger.info('Setting custom area from user list item', item.data)
          selectArea(item.data)
          break
      }
    },
    [isMapBuilderPage, selectAddress, selectLocation, chooseLocation, chooseAddress, inEntitySelector]
  )

  const handleClickSearch = () => {
    setFilter('')
    setGoogleSearchString(filter)
    setUserListsModalOpen(false)
  }

  if (filter && filteredList.length === 0) {
    return (
      <ListEmptyState
        heading="No results found"
        subheading={
          <Button variant="contained" fullWidth onClick={handleClickSearch}>
            Search '{filter}' in AlphaMap
          </Button>
        }
      />
    )
  }

  if (selectedList.items.length === 0) {
    return <ListEmptyState subheading="Once you save a property or custom area to this list, it will appear here." />
  }

  return (
    <UserListsWrapper>
      {selectedList.items.map((item) => (
        <UserListItem
          key={item.id}
          item={item}
          onClickHandler={handleClickItem}
          onClickDelete={handleClickDeleteItem}
          disabled={inEntitySelector && !listItemTypes.includes(item.type)}
          disabledTooltip={
            inEntitySelector && !listItemTypes.includes(item.type) ? `Unavailable for selection` : undefined
          }
        />
      ))}
      <Snackbar
        open={!!saveItemSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setSaveItemSuccessMessage(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <SnackContent>
          <CheckCircle size={18} color={Colors.primaryGreen400} />
          <Typography>{saveItemSuccessMessage}</Typography>
        </SnackContent>
      </Snackbar>
    </UserListsWrapper>
  )
}
